export const DEFAULT_INSTRUCTOR_TYPE = "BAZA";

export const INSTRUCTOR_TYPE_OPTIONS = [
   { value: "BAZA", label: "Bază" },
   { value: "SCHIMB", label: "Schimb" },
   { value: "EXAMEN", label: "Examen" },
   { value: "SUPERIOR", label: "Superior" },
];

export const INSTRUCTOR_TYPE_LABELS = INSTRUCTOR_TYPE_OPTIONS.reduce(
   (acc, option) => {
      acc[option.value] = option.label;
      return acc;
   },
   {},
);

export function normalizeInstructorType(value) {
   const raw = String(value || "").trim();
   if (!raw) return DEFAULT_INSTRUCTOR_TYPE;

   const upper = raw.toUpperCase();
   if (INSTRUCTOR_TYPE_LABELS[upper]) return upper;

   const normalized = raw
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toUpperCase();
   if (INSTRUCTOR_TYPE_LABELS[normalized]) return normalized;

   for (const [type, label] of Object.entries(INSTRUCTOR_TYPE_LABELS)) {
      const normalizedLabel = String(label || "")
         .normalize("NFD")
         .replace(/[\u0300-\u036f]/g, "")
         .toUpperCase();
      if (normalizedLabel === normalized) return type;
   }

   return DEFAULT_INSTRUCTOR_TYPE;
}

export function getInstructorTypeLabel(value) {
   return (
      INSTRUCTOR_TYPE_LABELS[normalizeInstructorType(value)] ||
      INSTRUCTOR_TYPE_LABELS[DEFAULT_INSTRUCTOR_TYPE]
   );
}

export function isSuperiorInstructorType(value) {
   return normalizeInstructorType(value) === "SUPERIOR";
}

export function isInstructorSelfProfileMode(value) {
   const mode = String(value || "")
      .trim()
      .toLowerCase();
   return mode === "superior-instructor" || mode === "instructor-self";
}

export function isSubstituteInstructorType(value) {
   return normalizeInstructorType(value) === "SCHIMB";
}

export const CALENDAR_MAX_GRID_INSTRUCTOR_TYPES = ["BAZA", "EXAMEN", "SUPERIOR"];

export function isCalendarMaxGridInstructorType(value) {
   return CALENDAR_MAX_GRID_INSTRUCTOR_TYPES.includes(
      normalizeInstructorType(value),
   );
}

export function resolveInstructorTypeFromRecord(inst) {
   return normalizeInstructorType(
      inst?.instructorType ?? inst?.instructor_type ?? inst?.type,
   );
}

export function isCalendarMaxGridInstructor(
   inst,
   { scopedInstructorId = "", lateralInstructorId = "1" } = {},
) {
   const id = String(inst?.id ?? inst?.instructorId ?? "").trim();
   if (!id) return false;
   if (id === String(lateralInstructorId)) return true;

   const scopedId = String(scopedInstructorId || "").trim();
   if (scopedId && id === scopedId) return true;

   return isCalendarMaxGridInstructorType(
      inst?.instructorType ?? inst?.instructor_type ?? inst?.type,
   );
}

/**
 * An instructor of type SCHIMB has no column of its own in the calendar — it is
 * only drawn as an overlay on the column of the instructor it covers for a day.
 * A reservation saved directly onto such an instructor therefore has no cell to
 * land in and silently disappears from the grid, so it must not be offered when
 * choosing who a lesson belongs to.
 */
export function canOwnCalendarReservations(inst) {
   if (!inst) return false;
   const isActive = inst?.isActive ?? inst?.is_active;
   if (isActive === false) return false;
   return isCalendarMaxGridInstructor(inst);
}

export function isExamInstructorProfile(instructor) {
   if (!instructor) return false;
   return (
      normalizeInstructorType(instructor.instructorType) === "EXAMEN" ||
      normalizeInstructorExam(instructor.isExamen)
   );
}

export function normalizeInstructorExam(value) {
   if (typeof value === "boolean") return value;
   if (typeof value === "number") return value === 1;

   const raw = String(value || "")
      .trim()
      .toLowerCase();

   return raw === "true" || raw === "1" || raw === "yes" || raw === "da";
}
